import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Loader2, Lock, CheckCircle2, AlertCircle, Sparkles, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";

const UpdatePassword: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUserPassword, loading: authLoading } = useAuth();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Check if user is authenticated (Supabase auto-logs them in via the reset link)
  useEffect(() => {
    // Give Supabase a moment to process the auth token from the URL
    const timer = setTimeout(() => {
      if (!authLoading && !user) {
        // User is not authenticated, redirect to login
        navigate("/", { replace: true });
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    const { error: updateError } = await updateUserPassword(newPassword);

    setLoading(false);

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess(true);
      // Redirect to home after success
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <Loader2 className="animate-spin text-emerald-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="text-emerald-400" size={32} />
              <h1 className="text-3xl font-bold text-white">Haseeb</h1>
            </div>
            <p className="text-emerald-200 text-sm">Update Your Password</p>
          </div>

          {success ? (
            // Success State
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-emerald-400" size={32} />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Password Updated!</h2>
              <p className="text-ink-primary text-sm mb-4">
                Your password has been successfully updated.
              </p>
              <p className="text-emerald-400 text-xs">Redirecting to home...</p>
            </motion.div>
          ) : (
            // Form State
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-secondary">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-secondary hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Confirm Password */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-secondary">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                  minLength={6}
                />
              </div>

              {/* Password Requirements */}
              <div className="text-xs text-ink-secondary px-1">
                <p>• Minimum 6 characters</p>
              </div>

              {/* Error Message */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-300 text-sm bg-red-500/20 border border-red-500/30 rounded-xl px-3 py-2"
                >
                  <AlertCircle size={16} />
                  {error}
                </motion.div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-on-fill rounded-xl font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="animate-spin" size={16} />}
                Update Password
              </button>

              {/* Back to Login */}
              <button
                type="button"
                onClick={() => navigate("/")}
                className="w-full py-2 text-ink-primary hover:text-white text-sm transition-colors"
              >
                Back to Login
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-ink-secondary text-xs mt-4">
          Built with 💚 for the Ummah
        </p>
      </motion.div>
    </div>
  );
};

export default UpdatePassword;

